import { PointerEvent, useMemo } from "react";
import {
  components,
  GroupBase,
  MenuListProps,
  Options,
  OptionsOrGroups,
} from "react-select";

import { useTranslate } from "../../../../i18n/hooks";
import { SelectOptionTree } from "../../types";
import { compareByProductCount, isGroup } from "../../utils";
import { OptionsTree } from "../OptionsTree";
import css from "./TovarTreeMenuList.module.scss";

// preventDefault на pointerdown по всей зоне меню (строки И промежутки между ними)
// удерживает фокус инпута react-select: иначе тап по пустому месту на тач уводит
// фокус в body, и react-select закрывает список по blur. На десктопе react-select
// делает так же на mousedown, но на тач mousedown эмулируется уже после blur —
// pointerdown срабатывает в начале касания. Скролл и клики при этом не блокируются.
const keepInputFocused = (e: PointerEvent) => e.preventDefault();

export const TovarTreeMenuList = <isMulti extends boolean = false>(
  props: MenuListProps<SelectOptionTree, isMulti>,
) => {
  const { t } = useTranslate();

  const withotGroup = useMemo(
    () => removeGroup(props.options),
    [props.options],
  );

  const filteredTree = useMemo(
    () =>
      filterTree(withotGroup, props.selectProps?.inputValue ?? "")
        ?.filter((node): node is NonNullable<typeof node> => node !== null)
        .slice()
        .sort(compareByProductCount),
    [props.selectProps?.inputValue, withotGroup],
  );

  const selectedIds = props.getValue().map((item) => item.value);
  const selectedKey = selectedIds.join(",");
  const selectedValues = useMemo(
    () => new Set(selectedIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedKey],
  );

  return (
    <components.MenuList {...props} className={css.menuList}>
      <div onPointerDown={keepInputFocused}>
        {filteredTree ? (
          filteredTree.map((item) => (
            <OptionsTree
              option={item}
              onSelect={props.selectOption}
              level={0}
              key={item.value}
              selectedValues={selectedValues}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore Заглушил, тк в доке тоже заглушили, хотел изначально передать тип, но ругается в родителе на тип (раньше все окей работало, в 4 версии)
              disableCondition={props.selectProps.disableCondition}
              expandAll={(props.selectProps?.inputValue ?? "").length > 1}
            />
          ))
        ) : (
          <div className={css.emptyTree}>
            {t("empty_tree", "Пустое дерево")}
          </div>
        )}
      </div>
    </components.MenuList>
  );
};

function removeGroup<Option>(
  optionsOrGroups: OptionsOrGroups<Option, GroupBase<Option>>,
): Options<Option> {
  const result = new Array<Option>();

  optionsOrGroups.forEach((item) => {
    if (isGroup(item)) {
      return result.push(...item.options);
    }

    result.push(item);
  });

  return result;
}

function filterTree(
  node: Options<SelectOptionTree> | undefined,
  filterText: string,
) {
  if (!node) {
    return null;
  }

  const filter = (
    node: SelectOptionTree,
  ): (SelectOptionTree & { options: SelectOptionTree[] }) | null => {
    const result: SelectOptionTree & { options: SelectOptionTree[] } = {
      ...node,
      options: [],
      isLeaf: !!node.options?.length,
    };

    if (node.options) {
      for (let i = 0; i < node.options.length; i++) {
        const children = filter(node.options[i]);

        if (children) result.options.push(children);
      }
    }

    if (!result.options.length) {
      const lowerText = filterText.toLowerCase();
      const find = (result.label + result.value)
        .toLowerCase()
        .includes(lowerText);

      return find ? result : null;
    }
    return result;
  };

  return node.map(filter);
}
